import { useContext, useEffect, useState } from 'react';
import { Box, Breadcrumbs, Typography, Table, TableContainer, Paper, TableHead, TableRow, TableCell, TableBody, Pagination, Modal } from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';
import dayjs from '../../../../config/dayjs';

import { useToaster } from '../../../../providers/ToasterProvider';
import { getVenueListByPage, deleteVenueById } from '../../../../services/venueMasterService';
import RadiusButton from '../../../../components/RadiusButton';

import SEARCH_ICON from '../../../../assets/images/search-icon.svg';
import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import ADD_USER from '../../../../assets/images/add-user.svg';
import PENCIL from '../../../../assets/images/pencil.svg';
import ACTION_DELETE from '../../../../assets/images/action-delete.svg';

import '../Users/Users.scss';
import { ProjectContext } from '../../../../providers/projectProvider';

const columns = [
    { id: 'venueName', label: 'Venue Name', minWidth: 240 },
    { id: 'createdAt', label: 'Created On', minWidth: 160 },
    { id: 'actions', label: 'Actions', minWidth: 190 },
];

const limit = process.env.REACT_APP_PAGE_LIMIT;
let timer = null;

// Venues an exam hall name must match when candidates are uploaded. Laid out like the Users list.
export const VenueMaster = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const { projectDetails } = useContext(ProjectContext);
    const { addNotification } = useToaster();
    const [maxPages, setMaxPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [venues, setVenues] = useState([]);
    const [venueToDelete, setVenueToDelete] = useState(null);
    const [search, setSearch] = useState('');
    const [newSearch, setNewSearch] = useState(false);

    useEffect(() => {
        const fetchVenues = async () => {
            try {
                const res = await getVenueListByPage(search, currentPage, limit);
                if (res.code === 'OK') {
                    setMaxPages(Math.max(Math.ceil(res.data.count / limit), 1));
                    setVenues(res.data.rows);
                }
            } catch (error) {
                console.log('There is an error while fetching venues', error);
            }
        }
        fetchVenues();
    }, [currentPage, newSearch]);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const onSearch = (e) => {
        setSearch(e.target.value);
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(() => {
            setCurrentPage(1);
            setNewSearch(!newSearch);
        }, 400);
    }

    const goToAddVenue = () => {
        navigate(`${location.pathname}/venue-master-details`);
    }

    const onConfirmDelete = async () => {
        if (!venueToDelete?.id) return;
        try {
            const res = await deleteVenueById(venueToDelete.id);
            if (res.code === 'OK') {
                setVenues(venues.filter((venue) => venue.id !== venueToDelete.id));
                addNotification('Venue has been deleted successfully.', 'success');
            }
        } catch (error) {
            // e.g. the venue is used by an exam hall
            addNotification(error?.response?.data?.message ?? 'Venue could not be deleted.', 'danger');
            console.log('There is an error while deleting venue', error);
        } finally {
            setVenueToDelete(null);
        }
    }

    const renderTableCell = (columnId, row) => {
        switch (columnId) {
            case 'actions':
                return (<>
                    <Link to={'venue-master-details'} state={{ venueId: row.id }}>
                        <img src={PENCIL} alt='' />
                        <Typography variant='body1' sx={{
                            display: 'inline-block',
                            verticalAlign: 'super',
                            ml: 1,
                            color: 'success.main'
                        }}>Edit</Typography>
                    </Link>
                    <Box component='span' sx={{
                        mx: 1,
                        cursor: 'pointer'
                    }} onClick={() => setVenueToDelete(row)}>
                        <img src={ACTION_DELETE} alt='' />
                        <Typography variant='body1' sx={{
                            display: 'inline-block',
                            verticalAlign: 'super',
                            ml: 1,
                            color: 'error.main'
                        }}>Delete</Typography>
                    </Box>
                </>);
            case 'createdAt':
                return row.createdAt ? dayjs(row.createdAt).format('DD-MM-YYYY') : '';
            default:
                return row[columnId];
        }
    }

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{ fontWeight: 400, textTransform: 'capitalize' }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>Venue Master</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container sx={{
                display: 'flex',
                flexDirection: 'column'
            }}>
                <h1 className='title'>
                    Venue Master
                </h1>

                <div className="search-header-wrapper">
                    <div className='search-wrapper'>
                        <input type='text' className='search-box' value={search} onChange={onSearch} placeholder='Search...' />
                        <img className='search-icon' src={SEARCH_ICON} alt='' />
                    </div>

                    <RadiusButton sx={{
                        textTransform: 'uppercase',
                        fontWeight: 500,
                    }} color="secondary"
                        variant='contained'
                        startIcon={<img src={ADD_USER} alt='' />}
                        onClick={goToAddVenue}
                        size='large'
                    >
                        Add Venue
                    </RadiusButton>
                </div>

                <Paper sx={{
                    width: '100%',
                    overflow: 'hidden',
                    bgcolor: 'background.base',
                    maxHeight: 'calc(100vh - 305px)',
                    maxWidth: 'calc(100vw - 140px)',
                    borderRadius: 1,
                    mb: 4
                }}>
                    <TableContainer sx={{
                        px: 1,
                        pb: 1,
                        maxHeight: 'calc(100vh - 365px)',
                        maxWidth: 'inherit',
                        border: '1px solid',
                        borderColor: 'primary.main',
                        borderRadius: 1
                    }}>
                        <Table stickyHeader aria-label="venue table">
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            sx={{ color: 'primary.main' }}
                                            key={column.id}
                                            align='center'
                                            style={{ minWidth: column.minWidth }}
                                        >
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!venues?.length
                                    ? <TableRow sx={{ mx: 'auto', p: 2, color: 'grey.500' }}>
                                        <TableCell colSpan={columns.length} sx={{ border: 'none', textAlign: 'center' }}>
                                            No records available
                                        </TableCell>
                                    </TableRow>
                                    : venues.map((row) => (
                                        <TableRow hover={false} key={row.id}>
                                            {columns.map((column) => (
                                                <TableCell key={column.id} align='center' sx={{
                                                    px: 1.5,
                                                    py: 0.75,
                                                    wordBreak: 'break-all'
                                                }}>
                                                    {renderTableCell(column.id, row)}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mt: 'auto'
                }}>
                    <Pagination count={maxPages} page={currentPage} onChange={handlePageChange} sx={{
                        ml: 'auto'
                    }} shape="rounded" />
                </Box>

                <Modal open={Boolean(venueToDelete)} hideBackdrop={true} className='backdrop' onClose={() => setVenueToDelete(null)}>
                    <Box className='modal-container' sx={{
                        maxWidth: 480,
                        p: 9,
                    }}>
                        <Typography variant='h6' sx={{
                            fontWeight: 400,
                            mb: 8,
                            textAlign: 'center'
                        }}>
                            Do you want to delete venue "{venueToDelete?.venueName}"?
                        </Typography>

                        <div className='actions-container' style={{ justifyContent: 'center', gap: '40px' }}>
                            <RadiusButton variant='outlined' onClick={() => setVenueToDelete(null)}>
                                No
                            </RadiusButton>
                            <RadiusButton variant='contained' onClick={onConfirmDelete}>
                                Yes
                            </RadiusButton>
                        </div>
                    </Box>
                </Modal>

            </Container>
        </MainContainer>
    );
}
